import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useApi } from './useApi';
import type { Priority } from './useReminders';

export interface KanbanLabel {
  name: string;
  color: string;
}

export interface ChecklistItem {
  text: string;
  done: boolean;
}

export interface KanbanCard {
  id: string;
  column_id: string;
  title: string;
  description: string | null;
  position: number;
  due_date: string | null;
  priority: Priority;
  labels: KanbanLabel[];
  checklist: ChecklistItem[];
  client_id: string | null;
  client_name?: string | null;
  client_logo?: string | null;
  completed: boolean;
}

export interface KanbanColumn {
  id: string;
  board_key: string;
  title: string;
  position: number;
  cards: KanbanCard[];
}

export function useKanban(boardKey: string) {
  const api = useApi();
  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api(`/kanban/${boardKey}`);
      setColumns(data.columns);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api, boardKey]);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Colonne ----
  const addColumn = useCallback(
    async (title: string) => {
      try {
        const col: KanbanColumn = await api(`/kanban/${boardKey}/columns`, { method: 'POST', body: JSON.stringify({ title }) });
        setColumns((prev) => [...prev, { ...col, cards: [] }]);
      } catch (e: any) {
        toast.error(e.message || 'Colonna non creata');
      }
    },
    [api, boardKey]
  );

  const renameColumn = useCallback(
    async (id: string, title: string) => {
      setColumns((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)));
      try {
        await api(`/kanban/columns/${id}`, { method: 'PATCH', body: JSON.stringify({ title }) });
      } catch (e: any) {
        toast.error(e.message || 'Rinomina fallita');
        await load();
      }
    },
    [api, load]
  );

  const deleteColumn = useCallback(
    async (id: string) => {
      const snapshot = columns;
      setColumns((prev) => prev.filter((c) => c.id !== id));
      try {
        await api(`/kanban/columns/${id}`, { method: 'DELETE' });
      } catch (e: any) {
        toast.error(e.message || 'Eliminazione colonna fallita');
        setColumns(snapshot);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, columns]
  );

  // ---- Card ----
  const addCard = useCallback(
    async (columnId: string, fields: Partial<KanbanCard> & { title: string }) => {
      try {
        const card: KanbanCard = await api(`/kanban/columns/${columnId}/cards`, {
          method: 'POST',
          body: JSON.stringify(fields),
        });
        setColumns((prev) => prev.map((c) => (c.id === columnId ? { ...c, cards: [...c.cards, card] } : c)));
        return card;
      } catch (e: any) {
        toast.error(e.message || 'Card non creata');
        throw e;
      }
    },
    [api]
  );

  const updateCard = useCallback(
    async (id: string, fields: Partial<KanbanCard>) => {
      try {
        const card: KanbanCard = await api(`/kanban/cards/${id}`, { method: 'PATCH', body: JSON.stringify(fields) });
        setColumns((prev) => prev.map((c) => ({ ...c, cards: c.cards.map((k) => (k.id === id ? card : k)) })));
      } catch (e: any) {
        toast.error(e.message || 'Aggiornamento card fallito');
        await load();
      }
    },
    [api, load]
  );

  const deleteCard = useCallback(
    async (id: string) => {
      const snapshot = columns;
      setColumns((prev) => prev.map((c) => ({ ...c, cards: c.cards.filter((k) => k.id !== id) })));
      try {
        await api(`/kanban/cards/${id}`, { method: 'DELETE' });
      } catch (e: any) {
        toast.error(e.message || 'Eliminazione card fallita');
        setColumns(snapshot);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, columns]
  );

  const moveCard = useCallback(
    async (id: string, toColumnId: string, toIndex: number) => {
      const snapshot = columns;
      // Optimistic: rimuovi la card e reinseriscila all'indice richiesto.
      let moving: KanbanCard | undefined;
      const stripped = columns.map((c) => {
        const found = c.cards.find((k) => k.id === id);
        if (found) moving = found;
        return { ...c, cards: c.cards.filter((k) => k.id !== id) };
      });
      if (moving) {
        const next = stripped.map((c) => {
          if (c.id !== toColumnId) return c;
          const cards = [...c.cards];
          cards.splice(Math.max(0, Math.min(toIndex, cards.length)), 0, { ...moving!, column_id: toColumnId });
          return { ...c, cards };
        });
        setColumns(next);
      }
      try {
        await api(`/kanban/cards/${id}/move`, { method: 'POST', body: JSON.stringify({ columnId: toColumnId, position: toIndex }) });
      } catch (e: any) {
        toast.error(e.message || 'Spostamento fallito');
        setColumns(snapshot);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, columns]
  );

  return {
    columns,
    loading,
    error,
    reload: load,
    addColumn,
    renameColumn,
    deleteColumn,
    addCard,
    updateCard,
    deleteCard,
    moveCard,
  };
}
